import { useLoadSwiper } from "@library/shared";
import { useEffect, useRef } from "react";
import { TABS_SPEED } from "../constants";
import type { TabTitleType, TabsPayload } from "../types";
import { useIntersection } from "./useIntersection";

declare const Swiper: any;

export const useInitSlider = ({
  id,
  isMobile,
  settings,
  isStaticView,
  titles,
  setActive,
  activeTabId,
}: {
  id: string;
  isMobile: boolean;
  settings: TabsPayload;
  isStaticView: boolean;
  titles?: TabTitleType[];
  setActive?: (id: string) => void;
  activeTabId?: string;
}) => {
  const layer = `[data-layer-id="${id}"]`;
  const isLoaded = useLoadSwiper();
  const isIntersection = useIntersection({ id });
  const { tabsAutoplay, tabsLoop, tabsSpeed } = settings;
  const autoplay =
    !isMobile && tabsAutoplay
      ? { autoplay: { delay: TABS_SPEED[tabsSpeed || "medium"] } }
      : {};

  // Refs for values we want to read inside the Swiper callback without
  // re-initializing the slider on every parent render. `titles` comes from a
  // non-memoized upstream call and would otherwise churn this effect.
  const titlesRef = useRef(titles);
  const setActiveRef = useRef(setActive);
  titlesRef.current = titles;
  setActiveRef.current = setActive;

  const initialSlide =
    titles && activeTabId
      ? Math.max(
          0,
          titles.findIndex((t) => t.id === activeTabId),
        )
      : 0;

  useEffect(() => {
    if (!isStaticView) return;
    if (!isLoaded || !isMobile) return;
    const contentSwiper = new Swiper(`${layer} .swiper-container-${id}`, {
      initialSlide,
      slidesPerView: "auto",
      spaceBetween: 16,
      autoHeight: false,
      observeSlideChildren: true,
      loop: tabsLoop,
      ...autoplay,
    });

    contentSwiper.on("slideChangeTransitionEnd", () => {
      const currentTitles = titlesRef.current;
      const notify = setActiveRef.current;
      if (!notify || !currentTitles?.length) return;
      const tab = currentTitles[contentSwiper.realIndex];
      if (tab) notify(tab.id);
    });

    if (!isMobile && tabsAutoplay && !isIntersection)
      contentSwiper.autoplay?.stop();
    if (!isMobile && isIntersection) contentSwiper.autoplay?.start();

    contentSwiper?.slides?.forEach((_: unknown, index: number) => {
      const slideEl = contentSwiper?.slides[index];
      if (slideEl) {
        new ResizeObserver(() => {
          contentSwiper?.update();
        }).observe(slideEl);
      }
    });

    return () => {
      if (contentSwiper) contentSwiper.destroy();
    };
  }, [id, isLoaded, isMobile]);
};
